import type { ReactNode } from 'react';
import { IconButton, Sidebar, Stepper } from '../design-system';
import { useIsDarkTheme, useTheme } from '../../hooks/useTheme';
import { JOURNEY_STEPS } from '../../lib/travelerJourney';

interface JourneySidebarProps {
  /** Index into JOURNEY_STEPS for the step the traveler is currently on. */
  activeIndex: number;
  /** Overrides for individual step copy (e.g. naming the active persona). */
  steps?: typeof JOURNEY_STEPS;
  footerItems?: { key: string; icon: string; label: string; onClick: () => void }[];
  footerSlot?: ReactNode;
}

/**
 * The journey stepper sidebar shared by the traveler funnel (/try → /matches →
 * /payment). Each screen previously did its own thing — TryChat had a bespoke
 * 3-step list, MatchSelection had no stepper, and Payment showed a small
 * JourneyProgress card — so the traveler lost their sense of place mid-flow.
 * One component, one canonical step list.
 */
export function JourneySidebar({ activeIndex, steps, footerItems, footerSlot }: JourneySidebarProps) {
  // `toggle` is a stateless action — safe to call from this instance. The
  // icon it renders must NOT read this instance's own `theme` state, in
  // case a `footerSlot` caller ever drops in another theme-owning control
  // (e.g. AccountMenu) alongside this one — see the staleness note on
  // useIsDarkTheme in useTheme.ts.
  const { toggle } = useTheme();
  const isDarkTheme = useIsDarkTheme();

  return (
    <div className="hidden md:flex shrink-0">
      <Sidebar
        style={{ height: '100%' }}
        headerActions={
          <IconButton icon={isDarkTheme ? 'sun' : 'moon'} label="Toggle theme" onClick={toggle} />
        }
        footerItems={footerItems}
        footerSlot={footerSlot}
      >
        <div style={{ padding: '4px 4px 0' }}>
          <Stepper activeIndex={activeIndex} steps={steps ?? JOURNEY_STEPS} />
        </div>
      </Sidebar>
    </div>
  );
}

export default JourneySidebar;
